import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getScenarios, deleteScenario } from '../api/lens'
import type { LensScenario, LensGoalType } from '../types'
import { Plus, Trash2, ChevronRight, Telescope } from 'lucide-react'
import { Button } from '../components/ui/button'

const GOAL_LABELS: Record<LensGoalType, string> = {
  classification: 'Classification',
  segmentation:   'Segmentation',
  association:    'Association',
  forecast:       'Forecast',
  anomaly:        'Anomaly',
}

const GOAL_COLORS: Record<LensGoalType, string> = {
  classification: 'bg-blue-50 text-blue-700 border-blue-100',
  segmentation:   'bg-violet-50 text-violet-700 border-violet-100',
  association:    'bg-amber-50 text-amber-700 border-amber-100',
  forecast:       'bg-emerald-50 text-emerald-700 border-emerald-100',
  anomaly:        'bg-red-50 text-red-700 border-red-100',
}

const STATUS_DOT: Record<string, string> = {
  done:    'bg-emerald-400',
  running: 'bg-amber-400 animate-pulse',
  queued:  'bg-ink-dim/40',
  failed:  'bg-red-400',
}

export function LensScenarios() {
  const navigate = useNavigate()
  const [scenarios, setScenarios] = useState<LensScenario[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getScenarios().then(setScenarios).finally(() => setLoading(false))
  }, [])

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    if (!confirm('Delete this scenario and all its runs?')) return
    await deleteScenario(id)
    setScenarios((s) => s.filter((x) => x.id !== id))
  }

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Telescope size={18} className="text-ink-dim" />
          <h1 className="text-xl font-semibold text-ink">Lens — ML Scenarios</h1>
        </div>
        <Button variant="primary" icon={Plus} onClick={() => navigate('/lens/scenarios/new')}>
          New scenario
        </Button>
      </div>

      {loading && <p className="text-sm text-ink-dim">Loading…</p>}

      {!loading && scenarios.length === 0 && (
        <div className="border border-dashed border-paper-border rounded-lg p-12 text-center">
          <Telescope size={28} className="text-ink-dim mx-auto mb-3" />
          <p className="text-sm font-medium text-ink-dim mb-1">No scenarios yet</p>
          <p className="text-xs text-ink-dim mb-4">Create a scenario to start training a model on your Rabbit data.</p>
          <Button variant="primary" size="sm" onClick={() => navigate('/lens/scenarios/new')}>
            New scenario
          </Button>
        </div>
      )}

      {!loading && scenarios.length > 0 && (
        <div className="space-y-2">
          {scenarios.map((s) => {
            const lastRun = s.last_run
            return (
              <div
                key={s.id}
                onClick={() => navigate(`/lens/scenarios/${s.id}`)}
                className="border border-paper-border rounded-lg px-4 py-3 hover:border-ink-dim/30 hover:shadow-sm cursor-pointer transition-all flex items-center gap-4 bg-white"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-ink truncate">{s.name}</span>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${GOAL_COLORS[s.goal_type]}`}>
                      {GOAL_LABELS[s.goal_type]}
                    </span>
                  </div>
                  <p className="text-theme-xs text-ink-dim">
                    Cup: <span className="text-ink-dim">{s.cup_title}</span>
                    {' · '}
                    {s.feature_traits.length} traits
                    {s.feature_relations.length > 0 && ` · ${s.feature_relations.length} relations`}
                  </p>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  {lastRun ? (
                    <div className="flex items-center gap-1.5 text-theme-xs text-ink-dim">
                      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[lastRun.status] ?? 'bg-ink-dim/40'}`} />
                      {lastRun.status === 'done' ? (
                        lastRun.applied_at ? 'Applied' : 'Ready to apply'
                      ) : lastRun.status}
                    </div>
                  ) : (
                    <span className="text-theme-xs text-ink-dim">No runs</span>
                  )}
                  <span className="text-theme-xs text-ink-dim">{s.run_count ?? 0} run{s.run_count !== 1 ? 's' : ''}</span>
                  <button
                    onClick={(e) => handleDelete(e, s.id)}
                    className="p-1 text-ink-dim hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={13} />
                  </button>
                  <ChevronRight size={14} className="text-ink-dim" />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
